import { useState } from 'react'
import type { FormEvent } from 'react'
import { useAuth } from '@shop/api-client'
import { BRAND } from '@shop/shared'
import { Banner, Button, ErrorBanner, Field, TextInput } from '@shop/ui'

/**
 * بابُ دخولِ نقطة البيع.
 *
 * <b>ولا قاعدةَ عملٍ فيه</b>: `login` هو نفسُه الذي تستعمله الإدارة، من
 * `@shop/api-client`. فالمنطقُ مكتوبٌ مرّةً — والمختلفُ هنا الشكلُ وحدَه.
 *
 * <b>وشكلُه مختلفٌ عمدًا:</b> شاشةُ الكاشير تُفتح في أوّل اليوم على جهازٍ
 * صغيرٍ وبيدٍ واحدة. فالحقلان كبيران، والزرُّ عريضٌ في الأسفل حيث يصله الإبهام.
 *
 * ⚠ ولا رسالةَ تُصاغ هنا: الخادمُ يردّ رمزًا واحدًا للاسم الخاطئ وللكلمة
 *   الخاطئة عمدًا، حتى لا يصير البابُ أداةً لعدّ موظّفينا.
 */
export default function Login() {
  const { login, endedByIdle } = useAuth()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    setError(null)

    try {
      await login(username.trim(), password)
    } catch (failure) {
      setError(failure)

      // الكلمةُ تُمحى عند الإخفاق: شاشةُ صندوقٍ تُترك مفتوحةً أمام الزبائن.
      setPassword('')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center px-4 py-8">
      <form className="w-full max-w-sm space-y-5" onSubmit={submit}>
        <div className="text-center">
          <h1 className="text-2xl font-bold">نقطةُ البيع</h1>
          <p className="mt-1 text-sm text-ink-soft">{BRAND.nameAr}</p>
        </div>

        <Field label="اسم المستخدم">
          <TextInput
            value={username}
            onChange={setUsername}
            name="username"
            autoComplete="username"
            dir="ltr"
            autoFocus
            disabled={busy}
          />
        </Field>

        <Field label="كلمة المرور">
          <TextInput
            value={password}
            onChange={setPassword}
            name="password"
            type="password"
            autoComplete="current-password"
            dir="ltr"
            disabled={busy}
          />
        </Field>

        {endedByIdle && !error && (
          <Banner tone="info">خرجتَ تلقائيًّا بعد ساعتين بلا استخدام — ادخل من جديد.</Banner>
        )}

        <ErrorBanner error={error} />

        <Button type="submit" tone="primary" wide disabled={busy || !username || !password}>
          {busy ? 'يُتحقَّق…' : 'دخول'}
        </Button>

        {/* ⚠ وهذا السطرُ ليس زينةً: الكاشيرُ الذي دخل الإدارةَ قبل قليلٍ سيظنّ
            أنّه داخلٌ هنا، فيقرأ لماذا يُسأل ثانيةً بدل أن يظنّ العطبَ في
            النظام. والسببُ أنّ كوكيّاتِ الجلسة مقصورةٌ على المضيف. */}
        <p className="text-center text-xs leading-relaxed text-ink-soft">
          دخولُ نقطة البيع مستقلٌّ عن تطبيق الإدارة — <b>الحسابُ نفسُه</b>،
          والجلسةُ لكلٍّ على حدة.
        </p>
      </form>
    </div>
  )
}
